// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { IDocument, PurchaseOrderProduct } from "../models"
import { ProcurementClient } from "./ProcurementClient"



interface InvoiceDescriptor {
  url: string | null
}


class Location {
  private client: ProcurementClient
  private data: any

  // For use with react-select
  public label: string
  public value: Location

  constructor(client: ProcurementClient, data: any) {
    this.client = client
    this.data = data
    this.value = this
    this.label = data.label
  }

  asDataTransferObject(): any {
    return this.data
  }

  getFormattedAddress(): string {
    return (
      `${this.data.address1}, `
      + `${this.data.postal_code}, ${this.data.city}, `
      + `${this.data.country_name}, Incoterms® 2020`
    )
  }
}


class TrackableDocument implements IDocument {
  private client: ProcurementClient
  private code: string
  private data: any

  constructor(client: ProcurementClient, code: string, data: any) {
    this.client = client
    this.code = code
    this.data = data
  }

  getBuyerName(): string | null {
    return null
  }

  getCurrency(): string {
    return this.data.currency
  }

  needsInvoice(): boolean {
    return true
  }

  hasInvoiceDocument(): boolean {
    return !!this.data.invoice.key
  }

  needsTrackingCode(): boolean {
    return !this.data.has_tracking_code
  }

  getContactName(): string {
    return this.data.contact_name
  }

  getContactEmail(): string {
    return this.data.contact_email
  }

  getDeliveryTerm(): string {
    return this.data.incoterms
  }

  getDeliveryAddress(): string {
    return (
      `${this.data.port_of_delivery.address1}, `
      + `${this.data.port_of_delivery.postalCode}, ${this.data.port_of_delivery.city}, `
      + `${this.data.port_of_delivery.country_name}`
    )
  }

  getDeliveryTermFormatted(): string {
    return (
      `${this.data.incoterms} ${this.data.port_of_delivery.address1}, `
      + `${this.data.port_of_delivery.postal_code}, ${this.data.port_of_delivery.city}, `
      + `${this.data.port_of_delivery.country_name}, Incoterms® 2020`
    )
  }

  getProducts(): any[] {
    //@ts-ignore
    return this.data.lines.map((dto: any, i: number) => new PurchaseOrderProduct(this, i, dto))
  }

  getOrderRef(): string | null {
    return this.data.reference || null
  }

  getInvoiceRef(): string | null {
    return this.data.invoice.ref || null
  }

  getTrackingCode(): string | null {
    return this.data.trackingcode || null
  }

  async getInvoiceUploadUrl(): Promise<string> {
    return await this.client.getInvoiceUploadUrl(this.data.id, this.code)
  }

  async setOrderRef(ref: string) {
    this.data.reference = ref
  }

  async setInvoiceRef(ref: string) {
    this.data.invoice.ref = ref
  }

  async setTrackingCode(trackingCode: string) {
    this.data.trackingcode = trackingCode
  }

  async uploadInvoice(file: File): Promise<void> {
    const uploadUrl = await this.getInvoiceUploadUrl()
    await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        'content-type': file.type,
        'content-length': `${file.size}`,
      }
    })
    await this.client.confirmInvoiceUpload(this.data.id, this.code, file)
    this.data = await this.client.getTrackableDocumentData(this.data.id, this.code)
  }

  async update() {
    await this.client.updateDocument(this.data.id, this.code, this.data)
  }
}


export {
  Location,
  TrackableDocument
}